<template>
  <div class="lottery-page">
    <!-- ============ 顶部信息 & 操作 ============ -->
    <div class="page-head">
      <div class="head-left">
        <div class="head-brand">
          <span class="brand-mark"><a-icon type="trophy" /></span>
          <div class="brand-text">
            <div class="head-title">
              抽签
              <span class="level-tag" v-if="levelType==1">中职组</span>
              <span class="level-tag" v-if="levelType==2">高职组</span>
              <span class="drawn-badge" v-if="isDrawn">
                <a-icon type="check-circle" /> 已抽签完成
              </span>
            </div>
            <div class="head-sub">
              <span class="stat-chip">备选题目 <b>{{ questionList.length }}</b> 道</span>
              <span class="stat-chip">参抽学校 <b>{{ mySchoolName }}</b></span>
              <!-- <span class="stat-chip" v-if="drawTime">抽签时间 {{ drawTime }}</span> -->
            </div>
          </div>
        </div>
      </div>
      <div class="head-right">
        <div class="head-actions">
          <div class="action-slot action-slot-mid">
            <a-button
              class="btn-ghost"
              icon="reload"
              :disabled="running"
              @click="initData"
              v-show="!isDrawn"
              >刷新题号</a-button
            >
          </div>
          <div class="action-slot action-slot-main">
            <a-button
              class="btn-main"
              type="primary"
              :loading="drawLoading"
              :disabled="running"
              v-show="!isDrawn"
              @click="startDraw"
            >
              <a-icon type="trophy" />
              {{ results.length ? "重新抽签" : "开始抽签" }}
            </a-button>
          </div>
        </div>
      </div>
    </div>

    <div class="main-row">
      <a-spin :spinning="poolLoading" class="main-col main-col-pool">
        <!-- ============ 题号池 ============ -->
        <div class="panel panel-pool">
          <div class="panel-title">
            <span class="bar"></span>
            <span class="panel-title-text">抽签题目</span>
            <span class="panel-tip"
              >共 {{ questionList.length }} 道，抽中高亮锁定</span
            >
          </div>
          <div
            class="pool-grid"
            :class="{ 'is-rolling': phase === 'rolling' }"
            v-if="questionList.length"
          >
            <div
              class="pool-item"
              v-for="(q, index) in questionList"
              :key="q.id"
              :class="{
                'is-scan': phase === 'rolling' && scanNo === q.orderNum,
                'is-hit': hitNos.indexOf(q.orderNum) !== -1,
              }"
              :style="{ animationDelay: index * 0.045 + 's' }"
            >
              <span class="pool-no">{{ q.orderNum }}</span>
              <span class="pool-name" :title="q.name">{{ q.name }}</span>
            </div>
          </div>
          <div class="pool-empty" v-else>
            <a-icon type="inbox" class="empty-ico" />
            <span>暂无题目数据</span>
          </div>
        </div>
      </a-spin>
    </div>

    <!-- ============ 抽签舞台 ============ -->
    <div class="stage" :class="['phase-' + phase, { 'is-running': running }]">
      <div class="stage-bg">
        <span class="aurora aurora-1"></span>
        <span class="aurora aurora-2"></span>
        <span class="aurora aurora-3"></span>
        <span class="grid"></span>
        <span class="spot"></span>
        <span class="stage-ring stage-ring-1"></span>
        <span class="stage-ring stage-ring-2"></span>
      </div>

      <!-- 待开始 -->
      <div class="stage-body" v-if="phase === 'idle'">
        <div class="idle-box">
          <div class="idle-ring">
            <a-icon type="scan" class="idle-ico" />
          </div>
          <div class="idle-title">准备就绪，等待抽签</div>
          <div class="idle-sub">点击右上角「开始抽签」，系统将依次为各学校随机抽取题目</div>
        </div>
      </div>

      <!-- 抽签中 -->
      <div class="stage-body" v-else-if="current">
        <div
          class="draw-card"
          :class="{ 'is-hit': phase === 'hit' }"
          :key="'card-' + currentIndex"
        >
          <span class="card-sweep"></span>
          <div class="card-top">
            <span class="seq-badge">NO.{{ currentIndex + 1 }}</span>
            <span class="progress-text"
              >{{ currentIndex + 1 }} / {{ schoolList.length }}</span
            >
          </div>

          <div class="school-name">{{ current.schoolName }}</div>

          <div
            class="slot"
            :class="{
              'is-enter': phase === 'enter',
              'is-rolling': phase === 'rolling',
              'is-hit': phase === 'hit',
            }"
          >
            <div class="slot-window">
              <div
                class="slot-strip"
                ref="strip"
                :style="{
                  transform: 'translate3d(0,' + -slotY + 'px,0)',
                  filter: 'blur(' + rollBlur + 'px)',
                }"
              >
                <div class="slot-item" v-for="(n, i) in strip" :key="i">
                  {{ n }}
                </div>
              </div>
              <div class="slot-mask"></div>
              <div class="slot-scan"></div>
              <div class="slot-line slot-line-top"></div>
              <div class="slot-line slot-line-bottom"></div>
            </div>
            <div class="slot-label">
              {{ phase === "hit" ? "" : "正在随机抽取…" }}
            </div>
          </div>

          <div class="question-name" :class="{ show: phase === 'hit' }">
            {{ current.name }}
          </div>
        </div>
      </div>

      <!-- 已结束：本校自行抽签只有一条结果，直接在这里展示抽中题目 -->
      <div class="stage-body" v-else-if="phase === 'done'">
        <div class="idle-box">
          <div class="done-icon"><a-icon type="trophy" /></div>
          <div class="done-title">抽签完成</div>
          <!-- 金色题号徽章 + 题目名称 -->
          <div class="done-result" v-if="myResult">
            <div class="dr-badge">
              <span class="dr-no">{{ myResult.orderNum }}</span>
            </div>
            <div class="dr-name" :title="myResult.name">
              {{ myResult.name }}
            </div>
          </div>
        </div>
      </div>

      <!-- 命中特效 -->
      <div class="burst" v-if="phase === 'hit'" :key="'b' + burstKey">
        <span class="ring"></span>
        <span class="ring r2"></span>
        <span class="ring r3"></span>
        <span class="flash"></span>
        <span class="shock"></span>
      </div>
      <div class="particles" v-if="phase === 'hit'" :key="'p' + burstKey">
        <i v-for="(p, i) in particles" :key="i" :style="p.style"></i>
      </div>

      <!-- 进度条 -->
      <div class="stage-progress" v-if="schoolList.length">
        <div
          class="stage-progress-inner"
          :style="{ width: progressPercent + '%' }"
        ></div>
      </div>
    </div>

  </div>
</template>

<script>
import {
  lxDrawChapterList,
  lxDrawResultDrawBySchool,
  lxDrawResultMyDrawStatList,
} from "@/api/index";

// 动画时长（毫秒），想调快慢改这里即可
const T_ENTER = 700; // 学校卡片入场
const T_ROLL = 3800; // 号码滚动（越长越有悬念）
const T_HIT = 1300; // 命中锁定展示
const T_GAP = 400; // 两所学校之间的间隔

// 滚动圈数：越多滚得越快越久
const ROLL_CYCLES = 6;

// 滚轮位移曲线：前段匀加速蓄力，后段长距离惯性减速停下（C1 连续，无突变）
const ACCEL_PART = 0.12; // 加速段占总时长比例
const DECEL_POW = 2.6; // 减速段指数，越大停得越"沉"
const PEAK_V =
  1 / (ACCEL_PART / 2 + (1 - ACCEL_PART) / (DECEL_POW + 1));
function slotEase(t) {
  if (t < ACCEL_PART) {
    return (PEAK_V * t * t) / (2 * ACCEL_PART);
  }
  const p = (t - ACCEL_PART) / (1 - ACCEL_PART);
  return (
    (PEAK_V * ACCEL_PART) / 2 +
    ((PEAK_V * (1 - ACCEL_PART)) / (DECEL_POW + 1)) *
      (1 - Math.pow(1 - p, DECEL_POW + 1))
  );
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// 章节库接口返回可能是数组，也可能是 { records: [...] }（jeecg 分页结构）
function pickRecords(res) {
  const r = res && res.result;
  if (Array.isArray(r)) return r;
  if (r && Array.isArray(r.records)) return r.records;
  return [];
}

// jeecg 统一返回：成功码 200；失败时 success=false 并带 message
function isOk(res) {
  if (!res) return false;
  if (res.success === false) return false;
  if (res.code !== undefined && res.code !== 200 && res.code !== 0) return false;
  return true;
}

function pad2(n) {
  return n < 10 ? "0" + n : String(n);
}

export default {
  name: "Lottery",
  data() {
    return {
      poolLoading: false,
      drawLoading: false,
      drawRound: "", // 届别/批次，按需调整
      questionList: [], // 题号池（章节库）：[{ id, orderNum, name }]
      schoolList: [], // 本校抽签结果（动画按这一条播放）
      results: [],
      drawTime: "", // 抽签时间（接口返回 createTime 时展示）
      isDrawn: false, // 本校是否已抽签完成（进页面时通过 myDrawStatList 判断）

      // 动画相关
      phase: "idle", // idle | enter | rolling | hit | done
      current: null,
      currentIndex: 0,
      strip: [],
      slotY: 0,
      rollBlur: 0,
      scanNo: "",
      hitNos: [],
      burstKey: 0,
      particles: [],
      rafId: null,
    };
  },
  computed: {
    // 组别由登录用户的学校类型决定，不再提供手动切换：1 中职 / 2 高职
    levelType() {
      const u = this.$store.state.user || {};
      const st = u.schoolType;
      if (st === 1 || st === "1") return "1";
      if (st === 2 || st === "2") return "2";
      return "1"; // 兜底按中职处理
    },
    // 当前登录学校名称（接口未返回 schoolName 时兜底展示）
    mySchoolName() {
      const u = this.$store.state.user || {};
      return u.realname || u.username || "本校";
    },
    // 本校抽签结果（学校自行抽签，只会有一条）
    myResult() {
      return this.results && this.results.length ? this.results[0] : null;
    },
    running() {
      return this.phase === "enter" || this.phase === "rolling" || this.phase === "hit";
    },
    progressPercent() {
      if (!this.schoolList.length) return 0;
      return Math.round((this.results.length / this.schoolList.length) * 100);
    },
  },
  created() {
    // 先加载题号池（需要用它把 drawChapterId 映射回题号），再查本校是否已抽签
    this.initData().then(() => this.checkMyDraw());
  },
  beforeDestroy() {
    if (this.rafId) cancelAnimationFrame(this.rafId);
  },
  methods: {
    // 接口1：获取题号池（章节库）—— 按当前组别 levelType 过滤
    async initData() {
      this.poolLoading = true;
      this.questionList = [];
      try {
        const res = await lxDrawChapterList({ levelType: this.levelType });
        if (isOk(res)) {
          const records = pickRecords(res);
          // 章节库字段兼容：中职(levelType=1)优先 sourceName，高职(levelType=2)优先 chapterName
          const isHighVocational = this.levelType === "2";
          this.questionList = records.map((c, i) => ({
            id: c.id,
            orderNum: c.orderNum, // 展厅显示的题号（01、02…），与抽中结果一一对应
            // 题目名称：中职用 sourceName（无则回退 chapterName），高职用 chapterName（无则回退 sourceName）
            name: isHighVocational
              ? c.chapterName || c.sourceName || ""
              : c.sourceName || c.chapterName || "",
            chapterName: c.chapterName || "",
          }));
          if (!this.questionList.length) {
            this.$message.info(this.levelType + "组别暂无启用的题号");
          }
        } else {
          this.$message.error((res && res.message) || "题号加载失败");
        }
      } catch (e) {
        console.error(e);
        this.$message.error("题号加载失败，请稍后重试");
      } finally {
        this.poolLoading = false;
      }
    },

    // 查询本校是否已抽签：GET lx/lxDrawResult/myDrawStatList
    // result 为 null 表示尚未抽签；否则为单个结果对象
    async checkMyDraw() {
      try {
        const res = await lxDrawResultMyDrawStatList({
          // levelType: this.levelType,
          // drawRound: this.drawRound,
        });
        if (!isOk(res)) {
          this.isDrawn = false;
          return;
        }
        const r = res && res.result;
        if (!r) {
          // 未抽签：明确切回待抽状态
          this.isDrawn = false;
          this.schoolList = [];
          this.results = [];
          this.hitNos = [];
          this.drawTime = "";
          this.phase = "idle";
          return;
        }
        // 约定返回单个对象；若后端给数组则取第一条
        const item = Array.isArray(r) ? r[0] : r;
        if (!item) {
          this.isDrawn = false;
          return;
        }
        this.isDrawn = true;
        const row = this.mapDrawItem(item);
        this.schoolList = [row];
        this.results = [row]; // 已完成：结果列表直接展示
        this.hitNos = [String(row.orderNum)].filter(Boolean); // 题号池高亮锁定
        this.drawTime = item.createTime || "";
        this.phase = "done";
      } catch (e) {
        // 查询失败不阻塞页面，保持可抽签状态
        console.error("查询本校抽签结果失败", e);
        this.isDrawn = false;
      }
    },

    // 把接口返回的单条抽签记录映射成页面展示结构（drawChapterId -> 题号）
    mapDrawItem(item) {
      const ch =
        this.questionList.find((q) => q.id === item.drawChapterId) || {};
      return {
        id: item.id,
        applyId: item.applyId,
        schoolName: item.schoolName || this.mySchoolName,
        drawChapterId: item.drawChapterId,
        drawNo: item.drawNo,
        orderNum: ch.orderNum || item.orderNum || item.drawNo || "", // 滚轮最终停在该题号
        name: ch.name || item.drawName || "",
        chapterName: ch.chapterName || "",
      };
    },

    // 接口2：本校抽签 —— POST lx/lxDrawResult/drawBySchool
    async startDraw() {
      if (this.running) return;
      if (!this.questionList.length) {
        this.$message.warning("当前组别暂无题号，无法抽签");
        return;
      }
      this.resetDraw();
      this.drawLoading = true;
      try {
        const res = await lxDrawResultDrawBySchool({
          // levelType: this.levelType,
          // drawRound: this.drawRound,
        });
        if (isOk(res)) {
          const r = res && res.result;
          // 约定返回单个对象；若后端给数组则取第一条
          const item = (Array.isArray(r) ? r[0] : r) || null;
          if (!item) {
            this.$message.warning("抽签未返回结果，请稍后重试");
            return;
          }
          const row = this.mapDrawItem(item);
          this.schoolList = [row];
          this.drawTime = item.createTime || "";
          await this.playSequence();
          this.isDrawn = true; // 本次抽签完成，顶部展示「已抽签完成」标识
        } else {
          this.$message.error((res && res.message) || "抽签失败");
        }
      } catch (e) {
        console.error(e);
        this.$message.error("抽签失败，请稍后重试");
      } finally {
        this.drawLoading = false;
      }
    },

    // 逐个学校播放抽签动画
    async playSequence() {
      for (let i = 0; i < this.schoolList.length; i++) {
        const item = this.schoolList[i];
        this.currentIndex = i;
        this.current = item;
        // 入场时先给一组乱序题号做模糊预览，滚动开始后替换成真正的滚动带
        this.strip = this.shuffle(this.questionList.map((q) => q.orderNum)).slice(0, 6);
        this.slotY = 0;
        this.rollBlur = 6; // 入场模糊，避免看清预览号码
        this.phase = "enter";
        await sleep(T_ENTER);

        this.phase = "rolling";
        await this.runSlot(item.orderNum);

        this.phase = "hit";
        this.burstKey++;
        this.particles = this.makeParticles();
        this.markHit(item.orderNum);
        await sleep(T_HIT);

        this.results.push(item);
        await sleep(T_GAP);
      }
      this.current = null;
      this.scanNo = "";
      this.phase = "done";
    },

    // 老虎机式滚动：蓄力加速 → 高速飞转 → 长距离惯性减速 → 精准停在接口返回的题号上
    runSlot(targetNo) {
      return new Promise((resolve) => {
        const pool = this.questionList.map((q) => q.orderNum);
        if (!pool.length) {
          resolve();
          return;
        }
        // 拼一条滚动带：随机乱序的题号循环若干轮 + 目标题号（末尾）+ 填充项
        let strip = [];
        for (let i = 0; i < ROLL_CYCLES; i++) {
          strip = strip.concat(this.shuffle(pool));
        }
        strip.push(targetNo);
        const targetIdx = strip.length - 1;
        strip = strip.concat(this.shuffle(pool).slice(0, 3));

        this.strip = strip;
        this.slotY = 0;
        this.rollBlur = 0;

        this.$nextTick(() => {
          const el = this.$refs.strip;
          if (!el) {
            resolve();
            return;
          }
          const first = el.firstElementChild;
          const itemH = (first && first.offsetHeight) || 56;
          const endY = Math.max((targetIdx - 1) * itemH, itemH);
          const start = performance.now();
          let lastY = 0;
          let lastTime = start;

          const tick = (now) => {
            const t = Math.min((now - start) / T_ROLL, 1);
            const y = slotEase(t) * endY;
            // 按实时速度算动态模糊：越快糊得越狠，慢慢停下时逐渐变清晰
            const dt = Math.max(now - lastTime, 1);
            const speed = (y - lastY) / dt; // px/ms
            lastY = y;
            lastTime = now;
            this.rollBlur = Math.min(10, Math.round(speed * 1.6 * 10) / 10);

            this.slotY = y;
            const idx = Math.max(
              0,
              Math.min(strip.length - 1, Math.round(y / itemH) + 1)
            );
            this.scanNo = strip[idx];

            if (t < 1) {
              this.rafId = requestAnimationFrame(tick);
            } else {
              this.slotY = endY;
              this.rollBlur = 0;
              this.scanNo = targetNo;
              resolve();
            }
          };
          this.rafId = requestAnimationFrame(tick);
        });
      });
    },

    // 命中粒子：分两批爆开，第一批是圆点，第二批是金色彩条
    makeParticles() {
      const colors = [
        "#ffd76a",
        "#ff9f43",
        "#e02020",
        "#ff5c8a",
        "#ffb020",
        "#ffffff",
      ];
      const total = 40;
      const list = [];
      for (let i = 0; i < total; i++) {
        const isBar = i >= 24; // 第二批：彩条，稍晚炸开、飞得近一些
        const angle = (Math.PI * 2 * i) / total + Math.random() * 0.42;
        const dist =
          (isBar ? 105 : 145) + Math.random() * (isBar ? 120 : 195);
        const size = 4 + Math.random() * 7;
        list.push({
          style: {
            "--tx": Math.cos(angle) * dist + "px",
            "--ty": Math.sin(angle) * dist + "px",
            "--rot": Math.round((Math.random() * 2 - 1) * 540) + "deg",
            background: colors[i % colors.length],
            width: size + "px",
            height: (isBar ? size * 2.1 : size) + "px",
            borderRadius: isBar ? "2px" : "50%",
            animationDelay:
              ((isBar ? 0.2 : 0) + Math.random() * 0.14).toFixed(2) + "s",
          },
        });
      }
      return list;
    },

    markHit(no) {
      if (this.hitNos.indexOf(no) === -1) this.hitNos.push(no);
    },

    shuffle(arr) {
      const list = arr.slice();
      for (let i = list.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        const tmp = list[i];
        list[i] = list[j];
        list[j] = tmp;
      }
      return list;
    },

    resetDraw(opts) {
      const keepDrawnFlag = opts && opts.keepDrawnFlag;
      if (this.rafId) cancelAnimationFrame(this.rafId);
      this.results = [];
      this.hitNos = [];
      this.strip = [];
      this.slotY = 0;
      this.rollBlur = 0;
      this.scanNo = "";
      this.current = null;
      this.currentIndex = 0;
      this.phase = "idle";
      this.drawTime = "";
      this.schoolList = [];
      if (!keepDrawnFlag) this.isDrawn = false;
    },
  },
};
</script>

<style scoped lang="scss">
.lottery-page {
  --c-ink: #2a1810;
  --c-muted: #8a6b55;
  --c-soft: #c4a48a;
  --c-line: #f0ddd0;
  --c-panel: rgba(255, 255, 255, 0.94);
  --c-red: #d61f1f;
  --c-red-deep: #9a0b00;
  --c-orange: #ff8a3c;
  --c-gold: #ffb020;
  --c-gold-soft: #ffd76a;
  --grad-brand: linear-gradient(135deg, #ff8a3c 0%, #e02020 100%);
  --grad-gold: linear-gradient(135deg, #ffd76a 0%, #ffb020 100%);
  --shadow-soft: 0 8px 20px rgba(154, 11, 0, 0.07);
  --shadow-lift: 0 14px 32px rgba(154, 11, 0, 0.12);
  --radius: 14px;

  min-height: 100%;
  padding: 16px 20px 24px;
  color: #333;
  font-size: 14px;
  /*background:
    radial-gradient(800px 360px at 8% -10%, rgba(255, 176, 32, 0.1), transparent 60%),
    radial-gradient(680px 320px at 96% 0%, rgba(224, 32, 32, 0.06), transparent 55%),
    #f5f5f5;*/
}

/* ---------- 顶部 ---------- */
.page-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  flex-wrap: wrap;
  padding: 14px 18px;
  margin-bottom: 14px;
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.head-brand {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.brand-mark {
  flex: 0 0 42px;
  width: 42px;
  height: 42px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  font-size: 20px;
  color: #fff;
  background: var(--grad-brand);
  box-shadow: 0 6px 16px rgba(224, 32, 32, 0.28);
}

.head-title {
  font-size: 22px;
  font-weight: 700;
  color: #1a1a1a;
  letter-spacing: 0.5px;
  line-height: 1.3;

  .level-tag {
    display: inline-block;
    margin-left: 8px;
    padding: 2px 10px;
    font-size: 12px;
    font-weight: 600;
    line-height: 20px;
    vertical-align: middle;
    color: #9a0b00;
    background: #fff0e6;
    border: 1px solid #ffd0c0;
    border-radius: 999px;
  }

  .drawn-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin-left: 8px;
    padding: 2px 12px;
    font-size: 13px;
    font-weight: 600;
    line-height: 20px;
    vertical-align: middle;
    color: #fff;
    background: var(--grad-brand);
    border-radius: 999px;
    box-shadow: 0 3px 10px rgba(224, 32, 32, 0.3);
  }
}

.head-sub {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 6px;
}

.stat-chip {
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  font-size: 13px;
  color: #666;
  background: rgba(255, 255, 255, 0.72);
  border: 1px solid rgba(255, 208, 176, 0.65);
  border-radius: 999px;

  b {
    color: #9a0b00;
    font-size: 15px;
    margin: 0 3px;
    font-variant-numeric: tabular-nums;
  }
}

.head-right {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 10px;
  justify-content: flex-end;
  flex-shrink: 0;
  margin-left: auto;
}

/* 组别：去掉外圈，仅保留标签 + 单选 */
.level-wrap {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: 0;
  flex-shrink: 0;
}

.head-actions {
  display: inline-flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 8px;
  flex-shrink: 0;
}

.action-slot {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  min-height: 34px;
  flex-shrink: 0;
}

.action-slot-mid {
  width: 148px;
}

.action-slot-main {
  width: 128px;
}

.action-slot .ant-btn {
  width: 100%;
}

.level-label {
  font-size: 14px;
  font-weight: 600;
  color: #595959;
  white-space: nowrap;
}

.level-group {
  .ant-radio-button-wrapper {
    height: 32px;
    line-height: 30px;
    padding: 0 16px;
    font-size: 14px;
    font-weight: 600;
    color: #c0392b;
    background: #fff;
    border-color: #ffd0c0;
    box-shadow: none;
    transition: all 0.2s ease;

    &::before {
      display: none;
    }
  }

  .ant-radio-button-wrapper:first-child {
    border-radius: 16px 0 0 16px;
  }

  .ant-radio-button-wrapper:last-child {
    border-radius: 0 16px 16px 0;
  }

  .ant-radio-button-wrapper:hover {
    color: var(--c-red);
  }

  .ant-radio-button-wrapper-checked,
  .ant-radio-button-wrapper-checked:hover {
    color: #fff;
    background: var(--grad-brand);
    border-color: #e02020;
    box-shadow: 0 3px 10px rgba(224, 32, 32, 0.28);
  }

  .ant-radio-button-wrapper-checked::before {
    display: none;
  }

  .ant-radio-button-wrapper[disabled] {
    color: #d9d9d9;
    background: #fafafa;
    border-color: #f0d9d2;
  }
}

.btn-ghost {
  height: 34px;
  border-radius: 999px;
  border-color: #ffd0c0;
  color: #b53a22;
  background: rgba(255, 255, 255, 0.85);
  font-weight: 600;
  font-size: 14px;
  box-shadow: 0 2px 6px rgba(224, 32, 32, 0.05);
  transition: all 0.2s ease;

  &:hover,
  &:focus {
    color: #fff !important;
    border-color: transparent !important;
    background: var(--grad-brand) !important;
  }
}

.btn-main {
  height: 34px;
  border-radius: 999px;
  padding: 0 20px;
  background: var(--grad-brand);
  border: none;
  font-weight: 700;
  font-size: 14px;
  letter-spacing: 0.3px;
  box-shadow: 0 6px 14px rgba(224, 32, 32, 0.3);
  transition: transform 0.2s ease, filter 0.2s ease, box-shadow 0.2s ease;

  &:hover,
  &:focus {
    filter: brightness(1.05);
    background: var(--grad-brand);
    transform: translateY(-1px);
    box-shadow: 0 8px 18px rgba(224, 32, 32, 0.36);
  }
}

/* 题号池 + 学校并排：两列卡片等高，内部条目不拉伸 */
.main-row {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  margin-bottom: 12px;
  align-items: stretch;
}

.main-col {
  min-width: 0;
  min-height: 0;
}

.main-col-pool {
  display: flex;
  flex-direction: column;
  height: 100%;

  /deep/ .ant-spin-nested-loading,
  ::v-deep .ant-spin-nested-loading,
  /deep/ .ant-spin-container,
  ::v-deep .ant-spin-container {
    height: 100%;
    display: flex;
    flex-direction: column;
    min-height: 0;
    flex: 1;
  }

  .panel-pool {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
    margin-bottom: 0;
  }
}

/* ---------- 通用面板 ---------- */
.panel {
  position: relative;
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 10px;
  padding: 14px 16px 16px;
  margin-bottom: 14px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 16px;
    right: 16px;
    height: 2px;
    border-radius: 2px;
    background: linear-gradient(90deg, transparent, rgba(255, 138, 60, 0.45), transparent);
    opacity: 0.7;
  }
}

.panel-title {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 2px 0;
  font-size: 16px;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 12px;

  .bar {
    width: 3px;
    height: 16px;
    border-radius: 2px;
    background: var(--grad-brand);
    margin-right: 8px;
  }

  .panel-title-text {
    letter-spacing: 0.3px;
  }

  .panel-tip {
    margin-left: 8px;
    font-size: 13px;
    font-weight: 400;
    color: #999;
  }

}
/* ---------- 题号池 ---------- */
.pool-grid {
  display: grid;
  /* 格子内要放「题号 + 题目名称」，改用自适应列宽 + 加高行高 */
  /* 一行 6 个（窄屏在下方媒体查询里递减列数） */
  grid-template-columns: repeat(6, 1fr);
  /* 固定行高，单行 / 两行名字行高一致（已按两行名字高度调好） */
  grid-auto-rows: 82px;
  align-content: start;
  align-items: start;
  gap: 8px;
  flex: 1 1 auto;
  min-height: 0;
  max-height: none;
  overflow-y: auto;
  /* 必须留内边距：高亮格子 .is-scan 会上移 4px 并放大 1.06 倍
     （向上外扩约 6px、左右各约 5px），而 overflow-y:auto 会让横向也一并裁剪，
     没有内边距时第一行顶部 / 首末列边缘会被切掉，看起来像"上面的线被挡住"。
     上移是向上的，所以顶部多留一点 */
  padding: 10px 8px 8px;

  &::-webkit-scrollbar {
    width: 5px;
  }
  &::-webkit-scrollbar-thumb {
    background: #ffd9b3;
    border-radius: 3px;
  }
}

.pool-item {
  position: relative;
  display: flex;
  /* 题号在上、题目名称在下 */
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  align-self: start;
  /* 撑满整行（82px），单行 / 两行名字卡片高度一致 */
  height: 100%;
  padding: 8px 10px;
  box-sizing: border-box;
  border-radius: 10px;
  text-align: center;
  background: linear-gradient(180deg, #ffffff 0%, #faf6f2 100%);
  border: 1px solid #efe2d6;
  transition: all 0.2s ease;
  /* 名称换行时卡片自然撑高，不再裁切 */
  overflow: visible;
  box-shadow: 0 1px 4px rgba(154, 11, 0, 0.03);
  .pool-no {
    display: block;
    font-size: 19px;
    font-weight: 800;
    color: #333;
    font-family: "DIN Alternate", "Segoe UI", "Microsoft YaHei", sans-serif;
    letter-spacing: 0.5px;
    font-variant-numeric: tabular-nums;
    line-height: 1.1;
  }
  .pool-name {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    /* 最多两行，保证所有卡片行高一致 */
    -webkit-line-clamp: 2;
    max-width: 100%;
    font-size: 14px;
    line-height: 1.3;
    color: #6b4a34;
    /* 显示不下就换行，不再单行省略号截断 */
    white-space: normal;
    word-break: break-word;
    overflow: hidden;
  }
}

.pool-item.is-scan {
  background: linear-gradient(135deg, #fff1e4, #ffe0c2);
  border-color: #ff9d4a;
  transform: translateY(-4px) scale(1.06);
  box-shadow: 0 10px 22px rgba(255, 138, 60, 0.32);
  z-index: 2;

  .pool-no {
    color: var(--c-red);
  }
}

.pool-item.is-hit {
  background: linear-gradient(135deg, #fff6e0, #ffe6b8);
  border-color: #ffc457;
  box-shadow: 0 8px 18px rgba(255, 173, 20, 0.28);

  .pool-no {
    color: #c47a00;
  }
}

.pool-item.is-hit::after {
  content: "已抽中";
  position: absolute;
  top: 0;
  right: 0;
  font-size: 11px;
  font-weight: 700;
  color: #fff;
  background: linear-gradient(135deg, #ffb020, #e8910c);
  padding: 2px 7px;
  border-bottom-left-radius: 8px;
}

.pool-grid.is-rolling .pool-item:not(.is-scan):not(.is-hit) {
  animation: poolBreath 0.7s ease-in-out infinite;
}

@keyframes poolBreath {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.48;
  }
}

.pool-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 28px;
  flex: 1;
  min-height: 120px;
  color: #999;
  font-size: 14px;

  .empty-ico {
    font-size: 28px;
    color: #ffc9a3;
  }
}

/* ---------- 舞台 ---------- */
.stage {
  position: relative;
  height: 360px;
  border-radius: 16px;
  overflow: hidden;
  margin-bottom: 12px;
  background:
    radial-gradient(900px 480px at 50% -18%, #ffffff 0%, #fff7ee 36%, #ffeddf 66%, #fde2cf 100%),
    #fff7ee;
  border: 1px solid #ffe1c9;
  box-shadow: var(--shadow-lift);
}

.stage.is-running {
  box-shadow: 0 22px 52px rgba(154, 11, 0, 0.2), 0 0 0 1px rgba(255, 176, 32, 0.25);
}

.stage-bg {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  overflow: hidden;
}

.aurora {
  position: absolute;
  border-radius: 50%;
  filter: blur(78px);
  opacity: 0.4;
  animation: float 16s ease-in-out infinite;
}

.aurora-1 {
  width: 430px;
  height: 430px;
  left: -90px;
  top: -130px;
  background: #ff9d5c;
}

.aurora-2 {
  width: 470px;
  height: 470px;
  right: -110px;
  top: -70px;
  background: #ff6b8a;
  opacity: 0.26;
  animation-delay: -5s;
}

.aurora-3 {
  width: 400px;
  height: 400px;
  left: 40%;
  bottom: -230px;
  background: #ffc94d;
  opacity: 0.42;
  animation-delay: -9s;
}

@keyframes float {
  0%,
  100% {
    transform: translate3d(0, 0, 0) scale(1);
  }
  33% {
    transform: translate3d(40px, 30px, 0) scale(1.12);
  }
  66% {
    transform: translate3d(-30px, -20px, 0) scale(0.94);
  }
}

.grid {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-image:
    linear-gradient(rgba(154, 11, 0, 0.05) 1px, transparent 1px),
    linear-gradient(90deg, rgba(154, 11, 0, 0.05) 1px, transparent 1px);
  background-size: 42px 42px;
  mask-image: radial-gradient(circle at 50% 45%, #000 30%, transparent 78%);
  -webkit-mask-image: radial-gradient(circle at 50% 45%, #000 30%, transparent 78%);
}

.spot {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: radial-gradient(circle at 50% 40%, rgba(255, 255, 255, 0.92), transparent 58%);
}

.stage-ring {
  position: absolute;
  left: 50%;
  top: 48%;
  border-radius: 50%;
  border: 1px solid rgba(224, 32, 32, 0.12);
  transform: translate(-50%, -50%);
  pointer-events: none;
}

.stage-ring-1 {
  width: 200px;
  height: 200px;
  animation: ringSpin 18s linear infinite;
}

.stage-ring-2 {
  width: 300px;
  height: 300px;
  border-style: dashed;
  border-color: rgba(255, 176, 32, 0.22);
  animation: ringSpin 28s linear infinite reverse;
}

@keyframes ringSpin {
  to {
    transform: translate(-50%, -50%) rotate(360deg);
  }
}

.stage-body {
  position: relative;
  z-index: 2;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.idle-box {
  text-align: center;
  color: #6b3a1f;
  padding: 0 20px;
}

.idle-ring {
  width: 72px;
  height: 72px;
  margin: 0 auto 12px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(224, 32, 32, 0.16);
  box-shadow: 0 10px 24px rgba(224, 32, 32, 0.14);
  animation: breathe 2.6s ease-in-out infinite;

  .idle-ico {
    font-size: 28px;
    color: var(--c-red);
  }
}

@keyframes breathe {
  0%,
  100% {
    transform: scale(1);
    box-shadow: 0 10px 24px rgba(224, 32, 32, 0.14);
  }
  50% {
    transform: scale(1.07);
    box-shadow: 0 16px 40px rgba(224, 32, 32, 0.28);
  }
}

.idle-title {
  font-size: 22px;
  font-weight: 700;
  letter-spacing: 1.5px;
  color: #333;
}

.idle-sub {
  margin-top: 8px;
  font-size: 14px;
  line-height: 1.5;
  color: #666;
}

.done-icon {
  width: 64px;
  height: 64px;
  margin: 0 auto 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-size: 28px;
  color: #fff;
  background: var(--grad-brand);
  box-shadow: 0 10px 24px rgba(224, 32, 32, 0.26);
  animation: pop 0.7s cubic-bezier(0.2, 1.5, 0.4, 1) both;
}

@keyframes pop {
  0% {
    transform: scale(0.2) rotate(-25deg);
    opacity: 0;
  }
  100% {
    transform: scale(1) rotate(0);
    opacity: 1;
  }
}

.done-title {
  font-size: 24px;
  font-weight: 800;
  letter-spacing: 2px;
  background: linear-gradient(90deg, #9a0b00, #e02020 48%, #b8860b);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

/* 抽签完成：本校抽中的题目（金色题号徽章 + 题目名称） */
.done-result {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  margin-top: 18px;
  padding: 22px 40px 24px;
  min-width: 240px;
  max-width: 480px;
  border-radius: 18px;
  background: linear-gradient(180deg, #fffdf8 0%, #fff4e0 100%);
  border: 1px solid rgba(255, 176, 32, 0.55);
  box-shadow: 0 16px 38px rgba(154, 11, 0, 0.16),
    inset 0 1px 0 rgba(255, 255, 255, 0.9);
  animation: doneResultIn 0.6s cubic-bezier(0.16, 1.02, 0.3, 1) both;

  .dr-badge {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 92px;
    height: 92px;
    border-radius: 50%;
    background: var(--grad-gold);
    box-shadow: 0 10px 26px rgba(255, 176, 32, 0.45),
      inset 0 -4px 10px rgba(154, 11, 0, 0.1);
    animation: drBadgePop 0.7s cubic-bezier(0.2, 1.5, 0.4, 1) 0.15s both;
  }

  .dr-no {
    font-size: 44px;
    font-weight: 800;
    line-height: 1;
    color: #9a0b00;
    font-family: "DIN Alternate", "Microsoft YaHei", sans-serif;
    font-variant-numeric: tabular-nums;
    text-shadow: 0 1px 0 rgba(255, 255, 255, 0.65);
  }

  .dr-name {
    max-width: 100%;
    font-size: 19px;
    font-weight: 700;
    letter-spacing: 1px;
    line-height: 1.4;
    color: #9a0b00;
    /* 名称较长时换行显示完整，不省略 */
    white-space: normal;
    word-break: break-word;
    overflow: visible;
    text-overflow: clip;
  }
}

@keyframes drBadgePop {
  0% {
    transform: scale(0.3) rotate(-18deg);
    opacity: 0;
  }
  100% {
    transform: scale(1) rotate(0);
    opacity: 1;
  }
}

@keyframes doneResultIn {
  0% {
    opacity: 0;
    transform: translateY(18px) scale(0.92);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.draw-card {
  position: relative;
  overflow: hidden;
  width: 520px;
  max-width: 92%;
  padding: 14px 22px 16px;
  border-radius: 16px;
  text-align: center;
  background: linear-gradient(160deg, rgba(255, 255, 255, 0.98), rgba(255, 255, 255, 0.88));
  border: 1px solid rgba(224, 32, 32, 0.14);
  backdrop-filter: blur(10px);
  box-shadow: 0 18px 40px rgba(154, 11, 0, 0.16), 0 2px 8px rgba(154, 11, 0, 0.05);
  transition: box-shadow 0.3s ease, border-color 0.3s ease;
  animation: cardIn 0.7s cubic-bezier(0.16, 1.02, 0.3, 1) both;
}

.draw-card.is-hit {
  border-color: #ffb020;
  box-shadow: 0 0 0 3px rgba(255, 176, 32, 0.32), 0 26px 62px rgba(154, 11, 0, 0.24);
}

.card-sweep {
  position: absolute;
  top: -12%;
  left: 0;
  width: 42%;
  height: 124%;
  z-index: 3;
  pointer-events: none;
  opacity: 0;
  border-radius: 18px;
  background: linear-gradient(
    105deg,
    transparent 0%,
    rgba(255, 215, 106, 0.7) 42%,
    rgba(255, 255, 255, 0.95) 52%,
    rgba(255, 215, 106, 0.7) 62%,
    transparent 100%
  );
  transform: translateX(-170%) skewX(-14deg);
}

.draw-card.is-hit .card-sweep {
  animation: sweep 1.05s cubic-bezier(0.3, 0.7, 0.3, 1) 0.05s both;
}

@keyframes sweep {
  0% {
    transform: translateX(-170%) skewX(-14deg);
    opacity: 0;
  }
  12% {
    opacity: 1;
  }
  88% {
    opacity: 1;
  }
  100% {
    transform: translateX(350%) skewX(-14deg);
    opacity: 0;
  }
}

@keyframes cardIn {
  0% {
    opacity: 0;
    transform: perspective(900px) rotateY(-42deg) translateY(28px) scale(0.9);
  }
  100% {
    opacity: 1;
    transform: perspective(900px) rotateY(0) translateY(0) scale(1);
  }
}

.card-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.seq-badge {
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 1px;
  color: #5a3a00;
  background: var(--grad-gold);
  padding: 4px 12px;
  border-radius: 999px;
  box-shadow: 0 3px 10px rgba(255, 176, 32, 0.35);
}

.progress-text {
  font-size: 14px;
  font-weight: 700;
  color: #666;
  letter-spacing: 1px;
  font-variant-numeric: tabular-nums;
  padding: 3px 10px;
  background: rgba(255, 244, 230, 0.9);
  border: 1px solid #ffe0c0;
  border-radius: 999px;
}

.school-name {
  margin: 8px 0 10px;
  font-size: 26px;
  font-weight: 800;
  letter-spacing: 1.5px;
  color: #1a1a1a;
  text-shadow: 0 2px 12px rgba(224, 32, 32, 0.1);
}
.slot {
  position: relative;
}

.slot-window {
  position: relative;
  height: 168px; /* 3 × 56px */
  border-radius: 12px;
  overflow: hidden;
  background: linear-gradient(180deg, #fff8ef 0%, #ffe8d1 100%);
  border: 1px solid rgba(224, 32, 32, 0.16);
  box-shadow: inset 0 2px 16px rgba(154, 11, 0, 0.12), 0 6px 16px rgba(154, 11, 0, 0.06);
  transition: box-shadow 0.4s ease, border-color 0.4s ease;
}

.slot-strip {
  will-change: transform, filter;
}

.slot-item {
  height: 56px;
  line-height: 56px;
  font-size: 40px;
  font-weight: 800;
  color: #7c2d12;
  letter-spacing: 3px;
  font-family: "DIN Alternate", "Segoe UI", "Microsoft YaHei", sans-serif;
  font-variant-numeric: tabular-nums;
}

.slot-mask {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  pointer-events: none;
  background: linear-gradient(
    180deg,
    rgba(255, 248, 240, 0.98) 0%,
    rgba(255, 248, 240, 0.6) 26%,
    rgba(255, 248, 240, 0) 46%,
    rgba(255, 248, 240, 0) 54%,
    rgba(255, 248, 240, 0.6) 74%,
    rgba(255, 248, 240, 0.98) 100%
  );
}

.slot-scan {
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  height: 56px;
  z-index: 2;
  pointer-events: none;
  opacity: 0;
  background: linear-gradient(
    180deg,
    transparent,
    rgba(255, 176, 32, 0.2) 38%,
    rgba(255, 255, 255, 0.7) 50%,
    rgba(255, 176, 32, 0.2) 62%,
    transparent
  );
}

.slot.is-rolling .slot-scan {
  opacity: 1;
  animation: scanMove 0.95s ease-in-out infinite;
}

@keyframes scanMove {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(112px);
  }
}

.slot-line {
  position: absolute;
  left: 12px;
  right: 12px;
  height: 1px;
  z-index: 2;
  background: linear-gradient(90deg, transparent, rgba(224, 32, 32, 0.55), transparent);
}

.slot-line-top {
  top: 56px;
}

.slot-line-bottom {
  top: 112px;
}

.slot.is-enter .slot-strip {
  opacity: 0.75;
}

.slot.is-rolling .slot-window {
  animation: shake 0.12s linear infinite;
  border-color: rgba(224, 32, 32, 0.5);
  box-shadow: inset 0 2px 20px rgba(154, 11, 0, 0.14), 0 0 32px rgba(224, 32, 32, 0.26);
}

@keyframes shake {
  0% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-1.5px);
  }
  100% {
    transform: translateY(0);
  }
}

.slot.is-hit .slot-window {
  border-color: #ff9d1c;
  box-shadow: 0 0 0 3px rgba(255, 176, 32, 0.35), 0 0 60px rgba(255, 146, 24, 0.55);
  animation: hitPop 0.9s cubic-bezier(0.2, 1.6, 0.35, 1);
}

@keyframes hitPop {
  0% {
    transform: scale(1);
  }
  35% {
    transform: scale(1.05);
  }
  60% {
    transform: scale(0.985);
  }
  100% {
    transform: scale(1);
  }
}

.slot.is-hit .slot-scan {
  opacity: 0;
}

.slot.is-hit .slot-item {
  color: #b91c1c;
  text-shadow: 0 0 26px rgba(255, 176, 32, 0.9), 0 2px 0 rgba(255, 255, 255, 0.7);
}

.slot-label {
  margin-top: 8px;
  font-size: 13px;
  letter-spacing: 2px;
  color: #666;
}

.question-name {
  margin-top: 6px;
  font-size: 17px;
  font-weight: 700;
  letter-spacing: 1px;
  color: #b91c1c;
  opacity: 0;
  transform: translateY(8px);
  transition: all 0.45s ease;
}

.question-name.show {
  opacity: 1;
  transform: translateY(0);
  text-shadow: 0 0 22px rgba(255, 176, 32, 0.45);
}

.burst {
  position: absolute;
  left: 50%;
  top: 50%;
  z-index: 3;
  pointer-events: none;
}

.burst .ring {
  position: absolute;
  left: 0;
  top: 0;
  width: 120px;
  height: 120px;
  margin: -60px 0 0 -60px;
  border-radius: 50%;
  border: 2px solid rgba(224, 32, 32, 0.8);
  animation: ringOut 1.15s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

.burst .ring.r2 {
  animation-delay: 0.16s;
  border-color: rgba(255, 157, 28, 0.85);
}

.burst .ring.r3 {
  animation-delay: 0.32s;
  border-color: rgba(255, 215, 106, 0.9);
}

@keyframes ringOut {
  0% {
    transform: scale(0.25);
    opacity: 0.95;
  }
  100% {
    transform: scale(4.6);
    opacity: 0;
  }
}

.burst .flash {
  position: absolute;
  left: 0;
  top: 0;
  width: 420px;
  height: 420px;
  margin: -210px 0 0 -210px;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    rgba(255, 255, 255, 0.92) 0%,
    rgba(255, 176, 32, 0.45) 32%,
    transparent 66%
  );
  animation: flashOut 0.9s ease-out forwards;
}

@keyframes flashOut {
  0% {
    transform: scale(0.35);
    opacity: 0.9;
  }
  100% {
    transform: scale(1.6);
    opacity: 0;
  }
}

.burst .shock {
  position: absolute;
  left: 0;
  top: 0;
  width: 60px;
  height: 60px;
  margin: -30px 0 0 -30px;
  border-radius: 50%;
  border: 8px solid rgba(255, 255, 255, 0.9);
  animation: shockOut 0.55s cubic-bezier(0.1, 0.8, 0.3, 1) forwards;
}

@keyframes shockOut {
  0% {
    transform: scale(0.3);
    opacity: 0.95;
    border-width: 8px;
  }
  100% {
    transform: scale(9);
    opacity: 0;
    border-width: 1px;
  }
}

.particles {
  position: absolute;
  left: 50%;
  top: 50%;
  z-index: 4;
  pointer-events: none;
}

.particles i {
  position: absolute;
  left: 0;
  top: 0;
  display: block;
  opacity: 0;
  animation: particleFly 1.25s cubic-bezier(0.15, 0.75, 0.3, 1) forwards;
}

@keyframes particleFly {
  0% {
    transform: translate(-50%, -50%) scale(0.2) rotate(0deg);
    opacity: 0;
  }
  16% {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1) rotate(0deg);
  }
  100% {
    transform: translate(calc(-50% + var(--tx)), calc(-50% + var(--ty)))
      scale(0.15) rotate(var(--rot));
    opacity: 0;
  }
}

.stage-progress {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 4px;
  background: rgba(154, 11, 0, 0.08);
  z-index: 3;
}

.stage-progress-inner {
  height: 100%;
  width: 0;
  background: linear-gradient(90deg, #9a0b00, #ff8a3c 60%, #ffd76a);
  box-shadow: 0 0 14px rgba(255, 140, 40, 0.75);
  transition: width 0.5s ease;
}

.import-modal {
  padding: 4px 2px;

  .import-steps {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .import-step {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 16px 18px;
    margin-bottom: 14px;
    background: linear-gradient(135deg, #fff7ef 0%, #fffaf3 100%);
    border: 1px solid #ffe2cf;
    border-radius: 14px;
    transition: box-shadow 0.2s ease, border-color 0.2s ease;

    &:hover {
      border-color: #ffb020;
      box-shadow: 0 6px 18px rgba(224, 32, 32, 0.08);
    }
  }

  .step-index {
    flex: 0 0 28px;
    width: 28px;
    height: 28px;
    line-height: 28px;
    text-align: center;
    border-radius: 50%;
    font-size: 14px;
    font-weight: 700;
    color: #fff;
    background: linear-gradient(135deg, #ff8a3c 0%, #e02020 100%);
    box-shadow: 0 3px 8px rgba(224, 32, 32, 0.25);
  }

  .step-text {
    flex: 1 1 auto;
    min-width: 0;
  }

  .step-title {
    font-size: 15px;
    font-weight: 600;
    color: #333;
  }

  .step-desc {
    margin-top: 2px;
    font-size: 13px;
    color: #999;
    line-height: 1.5;
  }

  .tpl-btn {
    flex: 0 0 auto;
    color: #fff !important;
    border: none !important;
    background: linear-gradient(135deg, #ff8a3c 0%, #e02020 100%) !important;
    box-shadow: 0 4px 12px rgba(224, 32, 32, 0.22);
    border-radius: 999px !important;

    &:hover,
    &:focus {
      color: #fff !important;
      background: linear-gradient(135deg, #ff9a52 0%, #ef3030 100%) !important;
      filter: brightness(1.02);
    }

    .anticon {
      color: #fff !important;
    }
  }

  .pick-btn {
    flex: 0 0 auto;
    color: #e02020 !important;
    border-color: #ffb020 !important;
    background: #fff !important;
    border-radius: 999px !important;

    &:hover {
      color: #fff !important;
      border-color: transparent !important;
      background: linear-gradient(135deg, #ff8a3c 0%, #e02020 100%) !important;
    }
  }

  .step-action {
    flex: 0 0 auto;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 8px;
  }

  .file-format-tip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 5px 11px;
    font-size: 12px;
    font-weight: 500;
    color: #c0561a;
    background: linear-gradient(135deg, #fff3e3 0%, #ffecd9 100%);
    border: 1px solid #ffd9b3;
    border-radius: 999px;
    white-space: nowrap;

    .anticon {
      color: #ff8a3c;
      font-size: 13px;
    }
  }

  .import-file-card {
    position: relative;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 16px 14px 20px;
    margin-top: 6px;
    background: linear-gradient(135deg, #fffaf4 0%, #fff6ec 100%);
    border: 1px solid #ffd9b3;
    border-radius: 14px;
    overflow: hidden;
    box-shadow: 0 2px 10px rgba(255, 138, 60, 0.1);

    &::before {
      content: "";
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 4px;
      background: linear-gradient(180deg, #ff8a3c 0%, #e02020 50%, #ffb020 100%);
    }

    .file-ico {
      position: relative;
      z-index: 2;
      flex: 0 0 38px;
      width: 38px;
      height: 38px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 20px;
      color: #fff;
      background: linear-gradient(135deg, #ff8a3c 0%, #e02020 100%);
      border-radius: 10px;
      box-shadow: 0 4px 10px rgba(224, 32, 32, 0.3);
    }

    .file-meta {
      position: relative;
      z-index: 2;
      flex: 1 1 auto;
      min-width: 0;
    }

    .file-name {
      font-size: 14px;
      font-weight: 600;
      color: #1a1a1a;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .file-sub {
      margin-top: 2px;
      font-size: 12px;
      color: #c08a4a;
    }

    .file-del {
      position: relative;
      z-index: 2;
      font-size: 18px;
      color: #d9b48f;
      cursor: pointer;
      transition: color 0.2s ease, transform 0.2s ease;

      &:hover {
        color: #e02020;
        transform: scale(1.12);
      }
    }
  }

  .import-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin-top: 4px;
    padding: 26px 0;
    border: 1px dashed #e3c9b8;
    border-radius: 14px;
    color: #c8a98f;
    font-size: 13px;
    background: #fffdfb;

    .anticon {
      font-size: 30px;
    }
  }
}

@media (max-width: 1200px) {
  .main-row {
    grid-template-columns: 1fr;
  }

  .pool-grid {
    max-height: 220px;
    /* 一行 6 个在中等屏会挤，降到 4 个 */
    grid-template-columns: repeat(4, 1fr);
  }

}

@media (max-width: 900px) {
  .page-head {
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .head-right {
    width: 100%;
    justify-content: flex-start;
    flex-wrap: wrap;
    margin-left: 0;
  }

  .head-actions {
    flex-wrap: wrap;
  }

  .action-slot-mid,
  .action-slot-main {
    width: auto;
    min-width: 110px;
  }

  .pool-grid {
    /* 小屏一行 2 个 */
    grid-template-columns: repeat(2, 1fr);
  }

  .stage {
    height: 340px;
  }

  .school-name {
    font-size: 22px;
  }
}
</style>

